import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Sitemap } from '../components/Sitemap';
import { NextPage } from '../components/NextPage';
import { BookIcon, VolumeIcon, CheckCircleIcon, PlayIcon, PauseIcon, LightbulbIcon, StarIcon, BrainIcon, PenIcon, HeadphonesIcon, ClockIcon, XIcon } from 'lucide-react';
const LESSON_DATA = {
  id: 1,
  title: {
    en: 'Introduction to English Sounds',
    ar: 'مقدمة في أصوات اللغة الإنجليزية'
  },
  description: {
    en: 'Learn the fundamental sounds of English letters and words',
    ar: 'تعلم الأصوات الأساسية للحروف والكلمات الإنجليزية'
  },
  duration: '15 minutes',
  durationAr: '15 دقيقة',
  sections: [{
    id: 'vowels',
    title: {
      en: 'Vowel Sounds',
      ar: 'أصوات الحروف المتحركة'
    },
    phrases: [{
      en: 'A as in "cat"',
      ar: 'A كما في "قط"',
      context: 'Short A sound',
      contextAr: 'صوت A القصير'
    }, {
      en: 'E as in "bed"',
      ar: 'E كما في "سرير"',
      context: 'Short E sound',
      contextAr: 'صوت E القصير'
    }, {
      en: 'I as in "sit"',
      ar: 'I كما في "يجلس"',
      context: 'Short I sound',
      contextAr: 'صوت I القصير'
    }, {
      en: 'O as in "hot"',
      ar: 'O كما في "ساخن"',
      context: 'Short O sound',
      contextAr: 'صوت O القصير'
    }, {
      en: 'U as in "cup"',
      ar: 'U كما في "كوب"',
      context: 'Short U sound',
      contextAr: 'صوت U القصير'
    }]
  }, {
    id: 'consonants',
    title: {
      en: 'Consonant Sounds',
      ar: 'أصوات الحروف الساكنة'
    },
    phrases: [{
      en: 'B as in "ball"',
      ar: 'B كما في "كرة"',
      context: 'B sound',
      contextAr: 'صوت B'
    }, {
      en: 'D as in "dog"',
      ar: 'D كما في "كلب"',
      context: 'D sound',
      contextAr: 'صوت D'
    }, {
      en: 'F as in "fish"',
      ar: 'F كما في "سمكة"',
      context: 'F sound',
      contextAr: 'صوت F'
    }, {
      en: 'G as in "go"',
      ar: 'G كما في "يذهب"',
      context: 'G sound',
      contextAr: 'صوت G'
    }]
  }],
  practice: [{
    question: {
      en: 'Which word has a short "A" sound?',
      ar: 'أي كلمة تحتوي على صوت "A" قصير؟'
    },
    options: [{
      en: 'Cat',
      ar: 'قط',
      correct: true
    }, {
      en: 'Cake',
      ar: 'كيك',
      correct: false
    }, {
      en: 'Car',
      ar: 'سيارة',
      correct: false
    }, {
      en: 'Care',
      ar: 'رعاية',
      correct: false
    }]
  }, {
    question: {
      en: 'Which consonant makes the sound in "fish"?',
      ar: 'أي حرف ساكن يصدر الصوت في "سمكة"؟'
    },
    options: [{
      en: 'F',
      ar: 'F',
      correct: true
    }, {
      en: 'P',
      ar: 'P',
      correct: false
    }, {
      en: 'V',
      ar: 'V',
      correct: false
    }, {
      en: 'B',
      ar: 'B',
      correct: false
    }]
  }],
  tips: [{
    en: 'Practice saying the sounds aloud to improve your pronunciation',
    ar: 'تدرب على نطق الأصوات بصوت عالٍ لتحسين نطقك'
  }, {
    en: 'Listen to native speakers to get familiar with the sounds',
    ar: 'استمع إلى المتحدثين الأصليين للتعرف على الأصوات'
  }, {
    en: 'Focus on the position of your mouth and tongue when making sounds',
    ar: 'ركز على وضع فمك ولسانك عند إصدار الأصوات'
  }]
};
export function LessonOne() {
  const {
    isArabic
  } = useLanguage();
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);
  const playAudio = (text: string) => {
    if (playingAudio === text) {
      window.speechSynthesis.cancel();
      setPlayingAudio(null);
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.onend = () => setPlayingAudio(null);
    setPlayingAudio(text);
    window.speechSynthesis.speak(utterance);
  };
  const handleAnswerSelect = (questionIndex: number, optionIndex: number) => {
    if (showResults) return;
    setSelectedAnswers({
      ...selectedAnswers,
      [questionIndex]: optionIndex
    });
  };
  const checkAnswers = () => {
    setShowResults(true);
  };
  const resetPractice = () => {
    setSelectedAnswers({});
    setShowResults(false);
  };
  const getCorrectAnswers = () => {
    return LESSON_DATA.practice.reduce((count, question, index) => {
      const selectedAnswer = selectedAnswers[index];
      if (selectedAnswer !== undefined && question.options[selectedAnswer].correct) {
        return count + 1;
      }
      return count;
    }, 0);
  };
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3326">
      <Sitemap currentPage={{
      en: `Lesson ${LESSON_DATA.id}: ${LESSON_DATA.title.en}`,
      ar: `الدرس ${LESSON_DATA.id}: ${LESSON_DATA.title.ar}`
    }} breadcrumbs={[{
      label: {
        en: 'Learn',
        ar: 'تعلم'
      },
      href: '/learn'
    }, {
      label: {
        en: 'Course 1',
        ar: 'الدورة 1'
      },
      href: '/learn/course/1'
    }]} data-id="element-3327" />
      <div className="max-w-4xl mx-auto" data-id="element-3328">
        <div className="mb-12" data-id="element-3329">
          <div className="flex items-center gap-4 mb-6" data-id="element-3330">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-3331">
              <BookIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3332" />
            </div>
            <div data-id="element-3333">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-3334">
                {isArabic ? LESSON_DATA.title.ar : LESSON_DATA.title.en}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-3335">
                {isArabic ? LESSON_DATA.description.ar : LESSON_DATA.description.en}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6" data-id="element-3336">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-3337">
              <ClockIcon className="w-5 h-5" data-id="element-3338" />
              <span data-id="element-3339">
                {isArabic ? LESSON_DATA.durationAr : LESSON_DATA.duration}
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-3340">
              <BrainIcon className="w-5 h-5" data-id="element-3341" />
              <span data-id="element-3342">
                {LESSON_DATA.sections.length} {isArabic ? 'أقسام' : 'sections'}
              </span>
            </div>
          </div>
        </div>
        {LESSON_DATA.sections.map(section => <div key={section.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-8" data-id="element-3343">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-3344">
              {isArabic ? section.title.ar : section.title.en}
            </h2>
            <div className="space-y-4" data-id="element-3345">
              {section.phrases.map((phrase, index) => <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-3346">
                  <div className="flex items-center justify-between mb-2" data-id="element-3347">
                    <div data-id="element-3348">
                      <div className="font-medium text-gray-900 dark:text-white text-lg" data-id="element-3349">
                        {phrase.en}
                      </div>
                      <div className="text-blue-600 dark:text-blue-400 text-lg" dir="rtl" data-id="element-3350">
                        {phrase.ar}
                      </div>
                    </div>
                    <button onClick={() => playAudio(phrase.en)} className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" data-id="element-3351">
                      {playingAudio === phrase.en ? <PauseIcon className="w-5 h-5" data-id="element-3352" /> : <VolumeIcon className="w-5 h-5" data-id="element-3353" />}
                    </button>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3354">
                    {isArabic ? phrase.contextAr : phrase.context}
                  </div>
                </div>)}
            </div>
          </div>)}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-8" data-id="element-3355">
          <div className="flex items-center gap-3 mb-6" data-id="element-3356">
            <PenIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3357" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3358">
              {isArabic ? 'تمارين' : 'Practice'}
            </h2>
          </div>
          <div className="space-y-8" data-id="element-3359">
            {LESSON_DATA.practice.map((question, qIndex) => <div key={qIndex} className="space-y-4" data-id="element-3360">
                <p className="text-lg text-gray-900 dark:text-white" data-id="element-3361">
                  {isArabic ? question.question.ar : question.question.en}
                </p>
                <div className="grid grid-cols-1 gap-3" data-id="element-3362">
                  {question.options.map((option, oIndex) => <button key={oIndex} onClick={() => handleAnswerSelect(qIndex, oIndex)} className={`
                        p-4 rounded-lg text-left transition-colors
                        ${selectedAnswers[qIndex] === oIndex ? showResults ? option.correct ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600'}
                      `} data-id="element-3363">
                      <div className="flex justify-between items-center" data-id="element-3364">
                        <div data-id="element-3365">
                          <div className="font-medium" data-id="element-3366">{option.en}</div>
                          <div className="text-sm opacity-75" data-id="element-3367">{option.ar}</div>
                        </div>
                        {showResults && (selectedAnswers[qIndex] === oIndex ? option.correct ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-3368" /> : <XIcon className="w-5 h-5 text-red-500" data-id="element-3369" /> : null)}
                      </div>
                    </button>)}
                </div>
              </div>)}
          </div>
          <div className="mt-6" data-id="element-3370">
            {showResults ? <div data-id="element-3371">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-4" data-id="element-3372">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-3373">
                    {isArabic ? 'نتائجك' : 'Your Results'}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300" data-id="element-3374">
                    {isArabic ? `لقد أجبت على ${getCorrectAnswers()} من ${LESSON_DATA.practice.length} بشكل صحيح` : `You got ${getCorrectAnswers()} out of ${LESSON_DATA.practice.length} correct`}
                  </p>
                </div>
                <button onClick={resetPractice} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-3375">
                  {isArabic ? 'حاول مرة أخرى' : 'Try Again'}
                </button>
              </div> : <button onClick={checkAnswers} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" disabled={Object.keys(selectedAnswers).length < LESSON_DATA.practice.length} data-id="element-3376">
                {isArabic ? 'تحقق من إجاباتك' : 'Check Answers'}
              </button>}
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-8 mb-8" data-id="element-3377">
          <div className="flex items-center gap-3 mb-6" data-id="element-3378">
            <LightbulbIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3379" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3380">
              {isArabic ? 'نصائح مفيدة' : 'Helpful Tips'}
            </h2>
          </div>
          <ul className="space-y-4" data-id="element-3381">
            {LESSON_DATA.tips.map((tip, index) => <li key={index} className="flex items-start gap-3" data-id="element-3382">
                <StarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-1" data-id="element-3383" />
                <span className="text-gray-800 dark:text-gray-200" data-id="element-3384">
                  {isArabic ? tip.ar : tip.en}
                </span>
              </li>)}
          </ul>
        </div>
        <NextPage currentPage={LESSON_DATA.id} totalPages={5} nextPageUrl={`/learn/course/1/lesson/${LESSON_DATA.id + 1}`} prevPageUrl={`/learn/course/1`} nextTitle={{
        en: 'Basic Greetings',
        ar: 'التحيات الأساسية'
      }} nextDescription={{
        en: 'Learn essential greetings and introductions',
        ar: 'تعلم التحيات والمقدمات الأساسية'
      }} data-id="element-3385" />
      </div>
    </div>;
}